import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Mail, Shield, Eye, Settings, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import api from '../services/api';

const WorkspaceMembers = ({ workspace, currentUser, onWorkspaceUpdate }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const isOwnerOrAdmin = workspace.owner_id === currentUser?.id || 
    members.find(m => m.user_id === currentUser?.id && ['admin', 'owner'].includes(m.role));

  useEffect(() => {
    if (isOpen) {
      loadMembers();
    }
  }, [isOpen, workspace.id]);

  const loadMembers = async () => {
    try {
      setLoading(true);
      const membersData = await api.workspaceMembers.getMembers(workspace.id);
      setMembers(membersData || []);
    } catch (error) {
      console.error('Failed to load members:', error);
      toast.error('Failed to load workspace members');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      setInviting(true);
      await api.workspaceMembers.invite(workspace.id, inviteEmail.trim(), inviteRole);
      toast.success('User invited successfully!');
      setInviteEmail('');
      setInviteRole('member');
      await loadMembers(); // Refresh the member list
    } catch (error) {
      console.error('Failed to invite user:', error);
      const message = error.response?.data?.detail || 'Failed to invite user';
      toast.error(message);
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.workspaceMembers.updateRole(workspace.id, userId, newRole);
      toast.success('Member role updated successfully!');
      await loadMembers(); // Refresh the member list
    } catch (error) {
      console.error('Failed to update role:', error);
      toast.error('Failed to update member role');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (userId === currentUser?.id) {
      toast.error('You cannot remove yourself from the workspace');
      return;
    }

    try {
      await api.workspaceMembers.removeMember(workspace.id, userId);
      toast.success('Member removed successfully!');
      await loadMembers(); // Refresh the member list
    } catch (error) {
      console.error('Failed to remove member:', error);
      toast.error('Failed to remove member');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      case 'editor':
        return 'bg-green-100 text-green-800';
      case 'member':
        return 'bg-gray-100 text-gray-800';
      case 'viewer':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner':
      case 'admin':
        return <Shield className="h-3 w-3" />;
      case 'viewer':
        return <Eye className="h-3 w-3" />;
      default:
        return <Users className="h-3 w-3" />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="flex items-center space-x-1">
          <Users className="h-4 w-4" />
          <span>Members</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>Manage Workspace Members</span>
          </DialogTitle>
          <DialogDescription>
            Invite users and manage member permissions for "{workspace.name}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Invite New Member */}
          {isOwnerOrAdmin && (
            <div className="border rounded-lg p-4 space-y-4">
              <h3 className="font-medium flex items-center space-x-2">
                <UserPlus className="h-4 w-4" />
                <span>Invite New Member</span>
              </h3>
              <form onSubmit={handleInviteUser} className="flex space-x-2">
                <div className="flex-1">
                  <Input
                    type="email"
                    placeholder="Enter email address"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    disabled={inviting}
                  />
                </div>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="submit" disabled={inviting}>
                  {inviting ? 'Inviting...' : 'Invite'}
                </Button>
              </form>
            </div>
          )}

          {/* Current Members */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center space-x-2">
              <Users className="h-4 w-4" />
              <span>Current Members ({members.length})</span>
            </h3>

            {loading ? (
              <div className="text-center py-4 text-gray-500">Loading members...</div>
            ) : members.length === 0 ? (
              <div className="text-center py-4 text-gray-500">No members found</div>
            ) : (
              <div className="space-y-2">
                {members.map((member) => (
                  <div
                    key={member.user_id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-600">
                          {member.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium">{member.name}</div>
                        <div className="text-sm text-gray-500 flex items-center space-x-1">
                          <Mail className="h-3 w-3" />
                          <span>{member.email}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Badge className={`${getRoleBadgeColor(member.role)} flex items-center space-x-1`}>
                        {getRoleIcon(member.role)}
                        <span className="capitalize">{member.role}</span>
                      </Badge>

                      {isOwnerOrAdmin && member.user_id !== currentUser?.id && member.role !== 'owner' && (
                        <div className="flex items-center space-x-1">
                          <Select
                            value={member.role}
                            onValueChange={(newRole) => handleRoleChange(member.user_id, newRole)}
                          >
                            <SelectTrigger className="w-24 h-8 text-xs">
                              <Settings className="h-3 w-3" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="viewer">Viewer</SelectItem>
                              <SelectItem value="member">Member</SelectItem>
                              <SelectItem value="editor">Editor</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveMember(member.user_id)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Role Descriptions */}
          <div className="border-t pt-4 space-y-2">
            <h4 className="font-medium text-sm text-gray-700">Role Permissions:</h4>
            <div className="text-xs text-gray-600 space-y-1">
              <div><strong>Owner:</strong> Full control of workspace and all members</div>
              <div><strong>Admin:</strong> Manage members and boards</div>
              <div><strong>Editor:</strong> Create and edit boards and cards</div>
              <div><strong>Member:</strong> View and contribute to boards</div>
              <div><strong>Viewer:</strong> Read-only access</div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WorkspaceMembers;